import { Stack } from 'expo-router';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useContext, useEffect, useMemo, useState } from 'react';
import { PaperProvider, MD3DarkTheme, MD3LightTheme } from 'react-native-paper';
import { GlobalStateProvider } from './context/GlobalState';
import JobsContext from './context/JobsContext';
import i18n from './i18n';
import { I18nextProvider } from 'react-i18next';
import { palettes } from './theme';
import { track } from './lib/analytics';
import { initializeAds } from './lib/ads';
import { checkForUpdatesAsync } from './lib/updates';
import ConsentBanner from './components/ConsentBanner';
import DisclaimerModal from './components/DisclaimerModal';

export const metadata = {
  title: 'RemoteWork - Find Remote Jobs Worldwide',
  description:
    'Discover remote job opportunities across industries. Search, filter, and apply to remote positions from top companies.',
  keywords: 'remote jobs, work from home, freelance, telecommute, job search',
  robots: 'index, follow',
  openGraph: {
    title: 'RemoteWork - Remote Job Search',
    description: 'Find your next remote job with RemoteWork.',
    images: [{ url: '/assets/images/icon.png' }],
  },
  twitter: {
    card: 'summary_large_image',
    title: 'RemoteWork - Remote Job Search',
    description: 'Find your next remote job with RemoteWork.',
    images: ['/assets/images/icon.png'],
  },
};

SplashScreen.preventAutoHideAsync();

function ThemedShell({ children }: { children: React.ReactNode }) {
  const { themeMode } = useContext(JobsContext);

  const paperTheme = useMemo(() => {
    const palette = palettes[themeMode];
    const base = themeMode === 'dark' ? MD3DarkTheme : MD3LightTheme;
    return {
      ...base,
      colors: {
        ...base.colors,
        primary: palette.primary,
        secondary: palette.secondary,
        background: palette.canvas,
        surface: palette.surface,
        surfaceVariant: palette.surfaceMuted,
        error: palette.danger,
        onPrimary: '#0F1115',
        onSecondary: palette.textOnDark,
        onBackground: palette.textPrimary,
        onSurface: palette.textPrimary,
      },
    };
  }, [themeMode]);

  return (
    <PaperProvider theme={paperTheme}>
      {children}
      <StatusBar style={themeMode === 'dark' ? 'light' : 'dark'} />
    </PaperProvider>
  );
}

// Brave's "aggressive fingerprinting protection" (and similar settings in
// other privacy-focused browsers) blocks font requests as fingerprinting
// vectors. Without a fallback, useFonts() never resolves and the whole app
// stays blank. Bail out after this many ms and render with the system font.
const FONT_TIMEOUT_MS = 3000;

export default function RootLayout() {
  const [loaded, fontError] = useFonts({
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    SpaceMono: require('../assets/fonts/SpaceMono-Regular.ttf'),
  });
  const [timedOut, setTimedOut] = useState(false);
  const ready = loaded || !!fontError || timedOut;

  useEffect(() => {
    const id = setTimeout(() => setTimedOut(true), FONT_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (ready) {
      SplashScreen.hideAsync();
      track('app_open');
      // Fire-and-forget. UMP consent dialog will appear over the UI when
      // ready; ad components mount without ads until SDK init resolves.
      initializeAds();
      // Fire-and-forget OTA check. If a newer JS bundle is available for this
      // build's runtime version it downloads and reloads the app into it;
      // otherwise it's a no-op (and always a no-op on web / dev / Expo Go).
      checkForUpdatesAsync();
    }
  }, [ready]);

  if (!ready) {
    return null;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <GlobalStateProvider>
        <ThemedShell>
          <Stack>
            <Stack.Screen name="index" options={{ headerShown: false }} />
            <Stack.Screen name="onboarding" options={{ headerShown: false }} />
            <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
            <Stack.Screen name="privacy_policy" options={{ title: 'Privacy Policy' }} />
            <Stack.Screen name="+not-found" />
          </Stack>
          <DisclaimerModal />
          <ConsentBanner />
        </ThemedShell>
      </GlobalStateProvider>
    </I18nextProvider>
  );
}
